import { useMemo } from "react"
import { useGenerationPrice } from "@/hooks/use-generation-price"
import {
  durationOptionsForProjectShotVideo,
  modelSupportsAdaptiveDuration,
  nearestDurationSec,
} from "@/lib/video-duration"
import { wanSupportsMultiShot } from "@/lib/wan-video-model"
import type { GenerationModelCatalogEntry, ProjectSummary } from "@/types/workspace"

type Args = {
  projectId: number
  project: ProjectSummary | null | undefined
  shotVideoModel: GenerationModelCatalogEntry | null
  shotVideoModels: GenerationModelCatalogEntry[] | null | undefined
  effectiveModelKey: string
  effectiveResolution: string
  durationTargetSec: number | null
  hasVideoReferenceInput: boolean
  seedanceAdaptiveDuration: boolean
  /** Звук шота: null — как в проекте. */
  generateAudioOverride?: boolean | null
}

/** Длительность, «Авто», цена и multi-shot для генерации видео шота. */
export function useShotVideoGenerationSettings({
  projectId,
  project,
  shotVideoModel,
  shotVideoModels,
  effectiveModelKey,
  effectiveResolution,
  durationTargetSec,
  hasVideoReferenceInput,
  seedanceAdaptiveDuration,
  generateAudioOverride = null,
}: Args) {
  const durationOptions = useMemo(() => {
    if (!project) return [5]
    if (shotVideoModel) {
      // Длительности от модели шота, если она переопределена.
      return durationOptionsForProjectShotVideo(
        { ...project, effective_video_model: shotVideoModel.id },
        shotVideoModels,
      )
    }
    return durationOptionsForProjectShotVideo(project, shotVideoModels)
  }, [project, shotVideoModel, shotVideoModels])
  const currentDuration = nearestDurationSec(durationTargetSec, durationOptions) ?? 5
  // «Авто» (Seedance editing): только модели 2.x и только при видео-референсе шота.
  const adaptiveAllowed = modelSupportsAdaptiveDuration(shotVideoModel) && hasVideoReferenceInput
  const adaptiveActive = adaptiveAllowed && seedanceAdaptiveDuration
  const videoPrice = useGenerationPrice({
    kind: "shot_video",
    projectId,
    params: {
      model_key: effectiveModelKey,
      resolution: effectiveResolution,
      duration_sec: adaptiveActive ? null : currentDuration,
      generate_audio: generateAudioOverride == null ? "" : generateAudioOverride,
    },
    enabled: !adaptiveActive && Boolean(effectiveModelKey),
  })
  const supportsMultiShot = wanSupportsMultiShot({
    effective_video_generation_provider: shotVideoModel?.provider ?? project?.effective_video_generation_provider,
    effective_video_model: shotVideoModel?.id ?? project?.effective_video_model,
  })
  return { durationOptions, currentDuration, adaptiveAllowed, adaptiveActive, videoPrice, supportsMultiShot }
}
